import { useQuery } from '@tanstack/react-query';

import { getDatasetEvals } from '~/api/experiments';
import type { DatasetEval } from '~/api/experiments/types';
import { parsePipelineVersion } from '~/scenes/Monitor/scenes/AgentLogs/utils/parsePipelineVersion';

export type AgentDatasetEvalsScope = {
  realm: string;
  pipelineName: string;
  pipelineVersion: string;
  connectorId: string;
  datasetId: string | null;
};

export const agentDatasetEvalsQueryKey = (
  scope: AgentDatasetEvalsScope & { version: number }
) =>
  [
    'agent-logs',
    'dataset-evals-for-collect',
    {
      realm: scope.realm,
      pipelineName: scope.pipelineName,
      version: scope.version,
      connectorId: scope.connectorId,
      datasetId: scope.datasetId
    }
  ] as const;

type UseAgentDatasetEvalsParams = AgentDatasetEvalsScope & {
  enabled?: boolean;
};

export function useAgentDatasetEvals({
  realm,
  pipelineName,
  pipelineVersion,
  connectorId,
  datasetId,
  enabled = true
}: UseAgentDatasetEvalsParams) {
  const version = parsePipelineVersion(pipelineVersion);

  return useQuery<DatasetEval[]>({
    queryKey: agentDatasetEvalsQueryKey({
      realm,
      pipelineName,
      pipelineVersion,
      connectorId,
      datasetId,
      version
    }),
    queryFn: async () => {
      if (!datasetId) {
        return [];
      }

      const response = await getDatasetEvals(
        realm,
        pipelineName,
        version,
        connectorId,
        datasetId
      );

      return response.content ?? [];
    },
    enabled:
      enabled &&
      !!datasetId &&
      !!realm &&
      !!pipelineName &&
      !!connectorId &&
      version > 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false
  });
}
